import { createUniqueId, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { RailItem } from '../lib/rail';
import { RailIcon } from './RailIcon';

// One rail ROW: a text link with an optional right-aligned mono count or a
// status dot. Presenter, split out of `AppNav` (one section) so each file is
// one component with one reason to change.
//
// The collapsed icon rail (#7710): the row's icon (`RailIcon.tsx`) is always in
// the DOM, `aria-hidden`, and shows ONLY while the rail is collapsed
// (`_sidebar.scss`), so the expanded rail stays text-only. The text is then
// visually hidden, never removed, so it stays the link's name; the count is
// hidden visually too and stays the link's description. `data-rail-tip` is what
// `RailTip` shows beside the icon: the label, then the count when it is known.

/** The dot's three tones (D-P's own verdict — `lib/terminal-status.ts`). */
export type NavDotStatus = 'ok' | 'degraded' | 'down';

/** The dot's word, per tone — a closed `Record`, so a fourth tone is a compile error here. */
export const DOT_LABEL: Readonly<Record<NavDotStatus, () => string>> = {
  ok: () => t('nav.dot_ok'),
  degraded: () => t('nav.dot_degraded'),
  down: () => t('nav.dot_down'),
};

export type AppNavItem = RailItem & {
  /**
   * A live count for the row ("Pending 6"). `undefined` — not loaded, refused,
   * or not honestly known — renders NO badge, never a 0: an unknown count shown
   * as zero tells the reader nothing is waiting when that is exactly what nobody
   * knows. A known 0 renders "0".
   */
  readonly count?: () => number | undefined;
  /**
   * A live status dot for the row (#4395: Terminal's WATCH row). The SAME
   * honesty rule `count` follows — `undefined` renders no dot at all, never a
   * guessed tone. Painted, not counted: a row carries `dot` XOR `count` today
   * (nothing needs both), so the row renders whichever is present.
   */
  readonly dot?: () => NavDotStatus | undefined;
};

export function AppNavLink(props: {
  readonly item: AppNavItem;
  readonly resolve: (href: string) => string;
  /** True on the ONE lit row of the whole rail (`currentRailItem`). */
  readonly lit: boolean;
}): JSX.Element {
  // The count is the link's DESCRIPTION, not part of its name: "Pending"
  // stays findable as "Pending" while the number changes under it, and a
  // screen reader still hears it after the name.
  const countId = createUniqueId();
  const dotId = createUniqueId();
  const count = (): number | undefined => props.item.count?.();
  const dot = (): NavDotStatus | undefined => props.item.dot?.();
  const tip = (): string => {
    const n = count();
    return n === undefined ? t(props.item.label) : `${t(props.item.label)} ${n}`;
  };
  // A plain anchor, not the router's `<A>`: `<A>` sets `aria-current` only
  // on an EXACT pathname and overwrites the caller's, so the row the rail
  // lights on a descendant route (`/repos/o/r`) was silent to a screen
  // reader. The router's delegated click handler still owns navigation (it
  // takes every same-origin anchor), and `link` is its native marker.
  return (
    <a
      link
      class="app-nav__link"
      classList={{ 'app-nav__link--active': props.lit }}
      href={props.resolve(props.item.href)}
      aria-current={props.lit ? 'page' : undefined}
      aria-describedby={
        [count() === undefined ? null : countId, dot() === undefined ? null : dotId]
          .filter((v): v is string => v !== null)
          .join(' ') || undefined
      }
      data-rail-tip={tip()}
    >
      <span class="app-nav__icon" aria-hidden="true">
        <RailIcon routeKey={props.item.key} size="1rem" />
      </span>
      <span class="app-nav__text">{t(props.item.label)}</span>
      {/* Same honesty rule as `count`: `undefined` (not loaded, refused,
          or genuinely unknown) renders no dot at all — never a guessed
          tone. The dot itself is decorative; `dotId`'s sr-only text
          carries the tone to assistive tech, the same split `count`
          draws between its visible badge and the link's own name. */}
      <Show when={dot()}>
        {(current) => (
          <>
            <span
              class="app-nav__dot"
              classList={{ [`app-nav__dot--${current()}`]: true }}
              aria-hidden="true"
            />
            <span id={dotId} class="sr-only">
              {DOT_LABEL[current()]()}
            </span>
          </>
        )}
      </Show>
      <Show when={count() !== undefined}>
        <span id={countId} class="app-nav__count" aria-hidden="true">
          {count()}
        </span>
      </Show>
    </a>
  );
}
